import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRetry } from './baseQuery';
import { TAG_ORG_MEMBER } from '@/store/tagTypes';

export interface OrgMemberDto {
  userId: string;
  email: string;
  name: string;
  picture: string | null;
  roles: string[];
}

export interface InviteUserRequest {
  email: string;
  role: string;
}

export interface ChangeUserRoleRequest {
  role: string;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [TAG_ORG_MEMBER],
  endpoints: (builder) => ({
    listOrgMembers: builder.query<OrgMemberDto[], void>({
      query: () => '/mgmt/users',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ userId }) => ({ type: TAG_ORG_MEMBER, id: userId })),
              { type: TAG_ORG_MEMBER, id: 'LIST' },
            ]
          : [{ type: TAG_ORG_MEMBER, id: 'LIST' }],
    }),

    inviteUser: builder.mutation<void, InviteUserRequest>({
      query: (body) => ({
        url: '/mgmt/users/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_ORG_MEMBER, id: 'LIST' }],
    }),

    removeUser: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/mgmt/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, userId) => [
        { type: TAG_ORG_MEMBER, id: userId },
        { type: TAG_ORG_MEMBER, id: 'LIST' },
      ],
    }),

    changeUserRole: builder.mutation<void, { userId: string; role: string }>({
      query: ({ userId, role }) => ({
        url: `/mgmt/users/${userId}/role`,
        method: 'PUT',
        body: { role },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: TAG_ORG_MEMBER, id: userId },
        { type: TAG_ORG_MEMBER, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListOrgMembersQuery,
  useInviteUserMutation,
  useRemoveUserMutation,
  useChangeUserRoleMutation,
} = usersApi;
